import 'numeral/locales/no';
import 'numeral/locales/pl';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Toaster, TooltipProvider } from '@ditioas/web-ui';
import { useSetAtom } from 'jotai';
import numeral from 'numeral';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation, withTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router';
import { SWRConfig } from 'swr';

import { setUserLanguageAtom } from 'hooks/useCurrentUserAndSettings';
import { get } from 'libs/apiClient';
import {
  cacheLocale,
  defaultLocale,
  getInitialLocale,
  locales,
} from 'model/locale';

import App from './app';
import ErrorBoundary from './components/errors/error-boundary';
import { getUserManager, signinSilentShared } from './libs/auth';
import MediaQueryProvider from './providers/MediaQueryProvider';
import PusherProvider from './providers/pusher-provider';

/*
  oicd init
*/
const userManager = getUserManager();

userManager.events.addUserSignedOut(() => {
  signinSilentShared().catch(() => {
    userManager.removeUser();
    userManager.signinRedirect();
  });
});

userManager.events.addSilentRenewError((error) => {
  const message = error?.message || error?.error || '';
  const requiresLogin =
    message.includes('login_required') ||
    message.includes('interaction_required') ||
    message.includes('invalid_grant');
  if (requiresLogin) {
    userManager.removeUser();
    userManager.signinRedirect();
  }
  // Transient errors (network, timeout) will be retried automatically by oidc-client
});

userManager.events.addAccessTokenExpired(() => {
  signinSilentShared().catch(() => {
    userManager.removeUser();
    userManager.signinRedirect();
  });
});

/*
  Init i18n
 */
numeral.defaultFormat('0,0.00');

const BoundaryWrapper = withTranslation()(({ t, ...rest }) => (
  <ErrorBoundary heading={t('Title.Global_error')}>
    <Content {...rest} />
  </ErrorBoundary>
));

const Content = ({ locale, ...rest }) => {
  const { t } = useTranslation();

  return (
    <SWRConfig
      value={{
        fetcher: (...args) =>
          get(...args).then((response) => {
            if (response?.status === 204) {
              const error = new Error(t('General.204Message'));
              error.response = response;
              throw error;
            }

            return response.json();
          }),
      }}
    >
      <PusherProvider>
        <BrowserRouter>
          <MediaQueryProvider>
            <DndProvider backend={HTML5Backend}>
              <TooltipProvider delayDuration={300}>
                <App {...rest} locale={locale} />
              </TooltipProvider>
            </DndProvider>
          </MediaQueryProvider>
        </BrowserRouter>
        <Toaster duration={1500} />
      </PusherProvider>
    </SWRConfig>
  );
};

Content.propTypes = {
  locale: PropTypes.string.isRequired,
};

const setDateAndNumberLocale = (userLocale) => {
  let locale = 'no';
  if (userLocale === locales.EN) {
    locale = 'en';
  } else if (userLocale === locales.PL) {
    locale = 'pl';
  } else if (userLocale === locales.SV) {
    // numeral has no sv locale; Norwegian separators match Swedish (space thousands, comma decimal)
    locale = 'no';
  }

  numeral.locale(locale);
};

const AppWrapper = ({ i18n }) => {
  // Matches the language i18n was initialised with — replaced by the user's
  // own language as soon as the current user is loaded.
  const [locale, setLocale] = useState(getInitialLocale);
  const setUserLanguage = useSetAtom(setUserLanguageAtom);

  useEffect(() => {
    setDateAndNumberLocale(locale);
  }, [locale]);

  const handleOnLocaleChange = (newLoc, persist = false) => {
    const isSupported = Object.values(locales).includes(newLoc);
    const newLocale = isSupported ? newLoc : defaultLocale;

    setLocale(newLocale);
    // Remembered for the next visit, so the app starts in the right language
    // before the current user has loaded (and on the logged-out screen).
    // Unsupported values are ignored: falling back to the default here must not
    // overwrite a language the browser or a previous session got right.
    if (isSupported) {
      cacheLocale(newLocale);
    }

    i18n.changeLanguage(newLocale);
    if (persist) {
      setUserLanguage(newLocale);
    }
  };

  return (
    <BoundaryWrapper onLocaleChange={handleOnLocaleChange} locale={locale} />
  );
};

AppWrapper.propTypes = {
  i18n: PropTypes.object.isRequired,
};

export default withTranslation()(AppWrapper);
